import { ELIGIBILITY_QUESTIONNAIRE_URL, readEligibilityCoverage } from '@bariendo/lib/eligibility-intake';
import { isInsuranceCoverage } from '@bariendo/lib/insurance-coverage';
import { InsurancePrefill, readInsurancePrefill } from '@bariendo/lib/insurance-prefill';
import { getReferenceString, MedplumClient } from '@medplum/core';
import { Organization, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { canSearchResourceType } from '@/services/permissions';
import { useEffect, useRef, useState } from 'react';

/**
 * Reads what signup recorded about the patient's insurance: the Coverage
 * bariendo-be wrote (source, payor, relationship), then the eligibility form's
 * labels for what it lacks. Returns nothing on any failure: a prefill is a
 * convenience, and the navigator asks either way.
 */
export async function readSignupInsurance(medplum: MedplumClient, patient: Patient): Promise<InsurancePrefill> {
  const subject = getReferenceString(patient);
  try {
    // Settled one by one: a refused Coverage read still leaves the form's answers.
    // A role whose policy has no Coverage search gets no Coverage, not a refused search.
    const [coverageRead, responseRead] = await Promise.allSettled([
      canSearchResourceType(medplum, 'Coverage')
        ? medplum.searchResources('Coverage', {
            beneficiary: subject,
            status: 'active',
            _sort: '-_lastUpdated',
            _count: 20,
          })
        : Promise.resolve([]),
      medplum.searchOne('QuestionnaireResponse', {
        subject,
        questionnaire: ELIGIBILITY_QUESTIONNAIRE_URL,
        _sort: '-authored',
      }),
    ]);
    const coverages = coverageRead.status === 'fulfilled' ? coverageRead.value : [];
    const response = responseRead.status === 'fulfilled' ? responseRead.value : undefined;
    const coverage = coverages.find(isInsuranceCoverage);
    const payorReference = coverage?.payor?.find((entry) => entry.reference?.startsWith('Organization/'));
    const payor = payorReference
      ? await medplum.readReference(payorReference as Reference<Organization>).catch(() => undefined)
      : undefined;
    return readInsurancePrefill(coverage, payor, readEligibilityCoverage(response));
  } catch {
    return {};
  }
}

/**
 * The signup insurance answers for the PN card's insurance section, read once
 * per patient while the note is being written. Undefined until read.
 */
export function useSignupInsurance(patient: Patient | undefined, enabled: boolean): InsurancePrefill | undefined {
  const medplum = useMedplum();
  const [prefill, setPrefill] = useState<InsurancePrefill | undefined>(undefined);
  const patientId = patient?.id;
  // Read once per patient: the Patient object changes on every live update.
  const patientRef = useRef(patient);
  patientRef.current = patient;

  useEffect(() => {
    const current = patientRef.current;
    if (!enabled || !current || !patientId) return undefined;
    let cancelled = false;
    void readSignupInsurance(medplum, current).then((result) => {
      if (!cancelled) setPrefill(result);
    });
    return () => {
      cancelled = true;
    };
  }, [medplum, enabled, patientId]);

  return prefill;
}
